import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Boxes, EllipsisVertical, Play, Trash2 } from "lucide-react";
import {
  ActionMenu,
  Button,
  Checkbox,
  DataTable,
  DateRangeInput,
  Field,
  FilterChip,
  localTablePage,
  Mono,
  PageContent,
  PageHeader,
  PageShell,
  StatusBadge,
  TableIdentity,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
  type StatusBadgeProps,
} from "@h2j-data-team/ui";
import {
  qvdAnalysisRunStatusSchema,
  type QvdAnalysisCoverage,
  type QvdAnalysisRun,
  type QvdAnalysisRunStatus,
  type StartQvdAnalysisInput,
} from "@repo/shared";

import { ConfirmDialog, useConfirmDialog, toast } from "@/components/shared";
import { supportCode } from "@/components/error-screen";
import { DensityToggle } from "@/components/density-toggle";
import { useTableDensity } from "@/lib/use-table-density";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { getErrorMessage } from "@/lib/api-client";
import { qvdCoveragePct } from "@/lib/health-format";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import {
  qvdAnalysisRunsInfiniteQueryOptions,
  useStartQvdAnalysis,
  useDeleteQvdAnalysis,
  isRunActive,
} from "@/features/qvd-analysis/api";
import { RunAnalysisDrawer, QvdScopeBadges } from "@/components/qvd-analysis";
import { useFormatDate } from "@/hooks/use-format-date";

export const Route = createFileRoute("/_authenticated/qvd-analyses/")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
    connections: typeof search.connections === "string" ? search.connections : undefined,
    status: typeof search.status === "string" ? search.status : undefined,
    dateFrom: typeof search.dateFrom === "string" ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === "string" ? search.dateTo : undefined,
  }),
  component: QvdAnalysesListPage,
});

const STATUS_TONE: Record<QvdAnalysisRunStatus, NonNullable<StatusBadgeProps["tone"]>> = {
  completed: "success",
  failed: "danger",
  cancelled: "neutral",
  cancelling: "warning",
  processing: "info",
  pending: "neutral",
};

// "All" is the empty list / undefined, never a sentinel: the table counts any
// other value as an active filter.
type RunFilters = {
  connections: string[];
  status: string[];
  dateFrom?: string;
  dateTo?: string;
};

function coveragePct(coverage: QvdAnalysisCoverage | null): string {
  if (!coverage) return "—";
  const pct = qvdCoveragePct(coverage);
  return pct === null ? "—" : `${pct}%`;
}

const csvToList = (csv: string | undefined): string[] =>
  csv
    ? csv
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

const listToCsv = (list: readonly string[]): string | undefined =>
  list.length === 0 ? undefined : list.join(",");

// A multi-valued filter as the library's chip over a popover of checkboxes: a
// MultiSelect would repeat the chosen values as tags beside its trigger, which
// crowds the toolbar. `summary` is empty for "all", which keeps the chip dashed.
function CheckboxFilter({
  label,
  clearLabel,
  options,
  value,
  onValueChange,
}: {
  label: string;
  clearLabel: string;
  options: readonly { value: string; label: string }[];
  value: readonly string[];
  onValueChange: (next: string[]) => void;
}) {
  const labels = value.map((v) => options.find((o) => o.value === v)?.label ?? v);
  const summary =
    labels.length === 0
      ? undefined
      : labels.length <= 2
        ? labels.join(", ")
        : `${labels[0]}, +${labels.length - 1}`;
  return (
    <FilterChip
      label={label}
      summary={summary}
      clearLabel={clearLabel}
      onClear={() => onValueChange([])}
    >
      <div role="group" aria-label={label} className="flex max-h-64 flex-col gap-2 overflow-y-auto">
        {options.map((o) => (
          <Field key={o.value} inline label={o.label}>
            <Checkbox
              checked={value.includes(o.value)}
              onCheckedChange={(on) =>
                onValueChange(
                  on === true ? [...value, o.value] : value.filter((v) => v !== o.value),
                )
              }
            />
          </Field>
        ))}
      </div>
    </FilterChip>
  );
}

function QvdAnalysesListPage() {
  const { t } = useTranslation("qvd-analysis");
  const { t: tc } = useTranslation("common");
  const { formatDateTime } = useFormatDate();
  const navigate = useNavigate({ from: Route.fullPath });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const runButton = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const tableRegion = useRef<HTMLDivElement>(null);
  useSearchHotkey(searchRef);
  const { density, setDensity } = useTableDensity();
  const { q, connections, status, dateFrom, dateTo } = Route.useSearch();
  // The search and the filters live in the URL; the page and its size here.
  const [paging, setPaging] = useState({ page: 1, pageSize: 50 });

  const updateSearch = useCallback(
    (patch: Record<string, string | undefined>) => {
      navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
    },
    [navigate],
  );

  const { data: connectionsData } = useQuery(connectionsQueryOptions);

  const connectionOptions = useMemo(
    () => (connectionsData ?? []).map((c) => ({ label: c.name, value: c.id })),
    [connectionsData],
  );
  const statusOptions = useMemo(
    () =>
      qvdAnalysisRunStatusSchema.options.map((s) => ({ value: s, label: t(`status.state.${s}`) })),
    [t],
  );

  const state: DataTableState<RunFilters> = {
    page: paging.page,
    pageSize: paging.pageSize,
    query: q ?? "",
    filters: {
      connections: csvToList(connections),
      status: csvToList(status),
      dateFrom,
      dateTo,
    },
    selection: [],
  };

  // Pass a single connectionId to the API only when exactly one is selected.
  const apiConnectionId =
    state.filters.connections.length === 1 ? state.filters.connections[0] : undefined;

  const runsQuery = useInfiniteQuery(
    qvdAnalysisRunsInfiniteQueryOptions({ limit: 100, connectionId: apiConnectionId }),
  );
  const { hasNextPage, fetchNextPage, isFetchingNextPage } = runsQuery;
  const startMutation = useStartQvdAnalysis();
  const deleteMutation = useDeleteQvdAnalysis();
  const { confirm, dialogProps } = useConfirmDialog();

  const connectionName = useCallback(
    (id: string) => connectionsData?.find((c) => c.id === id)?.name ?? id,
    [connectionsData],
  );

  const runs = useMemo(
    () => runsQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [runsQuery.data],
  );

  const onStateChange = (next: DataTableState<RunFilters>) => {
    setPaging({ page: next.page, pageSize: next.pageSize });
    const patch: Record<string, string | undefined> = {};
    if (next.query !== state.query) patch.q = next.query || undefined;
    if (next.filters !== state.filters) {
      const f = next.filters;
      if (listToCsv(f.connections) !== connections) patch.connections = listToCsv(f.connections);
      if (listToCsv(f.status) !== status) patch.status = listToCsv(f.status);
      if (f.dateFrom !== dateFrom) patch.dateFrom = f.dateFrom;
      if (f.dateTo !== dateTo) patch.dateTo = f.dateTo;
    }
    if (Object.keys(patch).length > 0) updateSearch(patch);
  };
  const changeTable = (patch: Partial<DataTableState<RunFilters>>) =>
    onStateChange(updateTableState(state, patch));

  const columns = useMemo<DataTableColumn<QvdAnalysisRun>[]>(
    () => [
      {
        id: "connection",
        header: t("list.cols.connection"),
        rowHeader: true,
        rowLink: true,
        variant: "identity",
        cell: (run) => (
          <TableIdentity
            leading={<Boxes />}
            name={
              <Link
                to="/qvd-analyses/$runId"
                params={{ runId: run.id }}
                search={{ tab: "orphans" }}
                className="rounded-sm hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {connectionName(run.connectionId)}
              </Link>
            }
          />
        ),
      },
      {
        id: "started",
        header: t("list.cols.started"),
        width: 180,
        cell: (run) => (
          <span className="text-dim">{run.startedAt ? formatDateTime(run.startedAt) : "—"}</span>
        ),
      },
      {
        id: "scope",
        header: t("list.cols.scope"),
        width: 260,
        wrap: true,
        cell: (run) => <QvdScopeBadges scope={run.scope} limit={2} />,
      },
      {
        id: "status",
        header: t("list.cols.status"),
        width: 140,
        cell: (run) => (
          <StatusBadge tone={STATUS_TONE[run.status]}>
            {t(`status.state.${run.status}`)}
          </StatusBadge>
        ),
      },
      {
        id: "orphans",
        header: t("list.cols.orphans"),
        width: 100,
        align: "end",
        variant: "numeric",
        cell: (run) => (run.evaluateOrphans ? (run.coverage?.orphanCount ?? "—") : "—"),
      },
      {
        id: "coverage",
        header: t("list.cols.coverage"),
        width: 120,
        align: "end",
        variant: "numeric",
        cell: (run) => (
          <span className="text-dim">{run.evaluateOrphans ? coveragePct(run.coverage) : "—"}</span>
        ),
      },
    ],
    [t, formatDateTime, connectionName],
  );

  // The API filters by one connection only; the rest (search, status, period,
  // several connections) narrows the runs loaded so far, here.
  const page = localTablePage(runs, state, {
    columns,
    search: (run) =>
      [connectionName(run.connectionId), ...run.scope.spaces.map((s) => s.spaceName)].join(" "),
    filter: (run, f) => {
      const fromTime = f.dateFrom ? new Date(f.dateFrom).getTime() : undefined;
      const toTime = f.dateTo ? new Date(f.dateTo).getTime() + 86_400_000 - 1 : undefined;
      const startedTime = run.startedAt ? new Date(run.startedAt).getTime() : null;
      return (
        (f.connections.length <= 1 || f.connections.includes(run.connectionId)) &&
        (f.status.length === 0 || f.status.includes(run.status)) &&
        (fromTime === undefined || (startedTime !== null && startedTime >= fromTime)) &&
        (toTime === undefined || (startedTime !== null && startedTime <= toTime))
      );
    },
  });

  const hasActiveFilters = !!q || !!connections || !!status || !!dateFrom || !!dateTo;

  const clearFilters = () =>
    changeTable({
      query: "",
      filters: { connections: [], status: [], dateFrom: undefined, dateTo: undefined },
    });

  const handleRun = (input: StartQvdAnalysisInput) => {
    startMutation.mutate(input, {
      onSuccess: ({ run, created }) => {
        setDrawerOpen(false);
        toast.success(t(created ? "toast.started" : "toast.alreadyRunning"));
        navigate({
          to: "/qvd-analyses/$runId",
          params: { runId: run.id },
          search: { tab: "orphans" },
        });
      },
      onError: (err) => toast.error(getErrorMessage(err, t("toast.startFailed"))),
    });
  };

  const handleDelete = (runId: string, label: string) => {
    confirm({
      title: t("delete.title"),
      description: t("delete.description", { connection: label }),
      confirmLabel: t("delete.confirmLabel"),
      variant: "destructive",
      onConfirm: async () => {
        await deleteMutation.mutateAsync(runId);
      },
    });
  };

  const code = runsQuery.isError
    ? supportCode(runsQuery.error, tc("error.supportCode"))
    : undefined;
  const runAction = (
    <Button size="sm" ref={runButton} onClick={() => setDrawerOpen(true)}>
      <Play />
      {t("run")}
    </Button>
  );

  return (
    <PageShell
      header={
        <PageHeader
          title={t("list.title")}
          description={t("list.description")}
          actions={runAction}
        />
      }
    >
      <PageContent fill>
        <DataTable
          label={t("list.title")}
          density={density}
          columns={columns}
          rows={page.rows}
          getRowId={(run) => run.id}
          regionRef={tableRegion}
          state={state}
          onStateChange={onStateChange}
          loading={runsQuery.isPending}
          refreshing={listRefreshing(runsQuery)}
          error={
            runsQuery.isError
              ? {
                  title: t("list.failedToLoad"),
                  description: code && (
                    <>
                      {code.label}: <Mono>{code.value}</Mono>
                    </>
                  ),
                }
              : null
          }
          onRetry={() => void runsQuery.refetch()}
          search={{
            ...tableSearch(t("list.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
          }}
          toolbar={
            <>
              <CheckboxFilter
                label={t("list.cols.connection")}
                clearLabel={t("list.clearFilter", { filter: t("list.cols.connection") })}
                options={connectionOptions}
                value={state.filters.connections}
                onValueChange={(next) =>
                  changeTable({ filters: { ...state.filters, connections: next } })
                }
              />
              <CheckboxFilter
                label={t("list.cols.status")}
                clearLabel={t("list.clearFilter", { filter: t("list.cols.status") })}
                options={statusOptions}
                value={state.filters.status}
                onValueChange={(next) =>
                  changeTable({ filters: { ...state.filters, status: next } })
                }
              />
              <DateRangeInput
                size="compact"
                aria-label={t("list.cols.started")}
                fromLabel={t("list.dateFrom")}
                toLabel={t("list.dateTo")}
                clearLabel={t("list.clearDates")}
                value={{ from: dateFrom, to: dateTo }}
                onValueChange={({ from, to }) =>
                  changeTable({
                    filters: {
                      ...state.filters,
                      dateFrom: from || undefined,
                      dateTo: to || undefined,
                    },
                  })
                }
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="compact" onClick={clearFilters}>
                  {t("list.clearFilters")}
                </Button>
              )}
              <DensityToggle value={density} onChange={setDensity} />
            </>
          }
          footer={
            hasNextPage ? (
              <div className="flex items-center gap-3">
                <p className="text-dim">{t("list.loadedCount", { count: runs.length })}</p>
                <Button
                  variant="outline"
                  size="sm"
                  loading={isFetchingNextPage}
                  onClick={() => void fetchNextPage()}
                >
                  {t("list.loadOlder")}
                </Button>
              </div>
            ) : undefined
          }
          pagination={{
            kind: "offset",
            total: page.total,
            labels: paginationLabels(),
          }}
          rowActions={{
            label: t("list.rowActions"),
            render: (run) => (
              <ActionMenu
                label={t("list.rowActionsFor", { name: connectionName(run.connectionId) })}
                trigger={
                  <Button
                    variant="ghost"
                    size="icon-compact"
                    aria-label={t("list.rowActionsFor", { name: connectionName(run.connectionId) })}
                  >
                    <EllipsisVertical />
                  </Button>
                }
                items={[
                  {
                    id: "delete",
                    label: t("list.deleteLabel"),
                    icon: <Trash2 />,
                    destructive: true,
                    disabled: deleteMutation.isPending || isRunActive(run.status),
                    onSelect: () => handleDelete(run.id, connectionName(run.connectionId)),
                  },
                ]}
              />
            ),
          }}
          messages={tableMessages({
            empty: {
              icon: <Boxes />,
              title: t("list.empty.title"),
              description: t("list.empty.description"),
              action: (
                <Button onClick={() => setDrawerOpen(true)}>
                  <Play />
                  {t("run")}
                </Button>
              ),
            },
            noResults: {
              title: t("list.noMatch.title"),
              description: t("list.noMatch.description"),
              action: (
                <Button variant="outline" onClick={clearFilters}>
                  {t("list.noMatch.clearFilters")}
                </Button>
              ),
            },
          })}
        />
      </PageContent>

      <RunAnalysisDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onRun={handleRun}
        running={startMutation.isPending}
        restoreFocusRef={runButton}
      />

      <ConfirmDialog
        {...dialogProps}
        isPending={deleteMutation.isPending}
        restoreFocusRef={tableRegion}
      />
    </PageShell>
  );
}
